/**
 * Part bundles tab of the Inventory page (#3350 phase 1).
 *
 * Lists named, parts-only bundles that can be inserted into a work-order or quote
 * service in one selection. Anyone who can read inventory sees the list; the create,
 * edit and delete actions follow `inventory.create`, `inventory.update` and
 * `inventory.delete`. A bundle holds no price: the work-order picker reads today's
 * catalog price when it inserts.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { DataTable, EntityListResults, type DataTableColumn } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PartBundleFormDialog } from '@/components/inventory/PartBundleFormDialog';
import {
  useDeletePartBundle,
  usePartBundlesPage,
  type PartBundleFilters,
  type PartBundleListItem,
} from '@/hooks/useInventory';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import { useAuthStore } from '@/stores/authStore';

export default function PartBundlesTab() {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canCreate = hasPermission('inventory.create');
  const canUpdate = hasPermission('inventory.update');
  const canDelete = hasPermission('inventory.delete');

  const [search, setSearch] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [formState, setFormState] = useState<{ bundleId: string | null } | null>(null);
  const [toDelete, setToDelete] = useState<PartBundleListItem | null>(null);

  useEffect(() => {
    if (search === debouncedQuery) return;
    const handle = setTimeout(() => setDebouncedQuery(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search, debouncedQuery]);

  const filters = useMemo(() => {
    const next: PartBundleFilters = {};
    if (debouncedQuery) next.q = debouncedQuery;
    return next;
  }, [debouncedQuery]);

  const bundles = usePartBundlesPage(filters);
  const deleteBundle = useDeletePartBundle();

  const confirmDelete = () => {
    if (!toDelete) return;
    const target = toDelete;
    deleteBundle.mutate(target.id, {
      onSuccess: () => {
        notify.success(t('bundles.notify.deleted'), t('bundles.notify.deletedDesc', { name: target.name }));
        setToDelete(null);
      },
      onError: (error) => {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('bundles.errors.deleteFailed') }));
        setToDelete(null);
      },
    });
  };

  const itemSummary = (bundle: PartBundleListItem) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2">{t('bundles.itemCount', { count: bundle.itemCount })}</Typography>
      {bundle.unavailableCount > 0 && (
        <Chip
          size="small"
          color="warning"
          label={t('bundles.unavailableCount', { count: bundle.unavailableCount })}
        />
      )}
    </Box>
  );

  const actions = (bundle: PartBundleListItem) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
      {canUpdate && (
        <Tooltip title={tc('edit')}>
          <IconButton
            size="small"
            onClick={() => setFormState({ bundleId: bundle.id })}
            aria-label={t('bundles.editNamed', { name: bundle.name })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title={tc('delete')}>
          <IconButton
            size="small"
            color="error"
            onClick={() => setToDelete(bundle)}
            aria-label={t('bundles.deleteNamed', { name: bundle.name })}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const columns: DataTableColumn<PartBundleListItem>[] = [
    {
      header: t('bundles.colName'),
      renderCell: (bundle) => (
        <Typography variant="body2" fontWeight={600}>
          {bundle.name}
        </Typography>
      ),
    },
    {
      header: t('bundles.colDescription'),
      renderCell: (bundle) => (
        <Typography variant="body2" color="text.secondary">
          {bundle.description ?? ''}
        </Typography>
      ),
    },
    { header: t('bundles.colItems'), renderCell: itemSummary },
    ...(canUpdate || canDelete
      ? [{ header: t('actions'), align: 'right' as const, renderCell: actions }]
      : []),
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('bundles.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('bundles.subtitle')}
          </Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormState({ bundleId: null })}>
            {t('bundles.add')}
          </Button>
        )}
      </Box>

      <TextField
        size="small"
        placeholder={t('bundles.searchPlaceholder')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ width: { xs: '100%', md: 500 }, mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <Tooltip title={t('clearSearch')}>
                  <IconButton size="small" onClick={() => setSearch('')} aria-label={t('clearSearch')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      <EntityListResults
        loading={bundles.isLoading}
        isError={bundles.isError}
        errorText={t('bundles.loadFailed')}
        error={bundles.error}
        onRefetch={() => void bundles.refetch()}
        resultCountText={t('searchResultCount', { count: bundles.total })}
        pagination={bundles.pagination}
      >
        <DataTable<PartBundleListItem>
          columns={columns}
          data={bundles.rows}
          getRowKey={(bundle) => bundle.id}
          loading={bundles.isLoading}
          ariaLabel={t('bundles.title')}
          emptyMessage={t('bundles.empty')}
          emptyDescription={canCreate ? t('bundles.emptyDescription') : t('bundles.emptyDescriptionReadOnly')}
          renderMobileCard={(bundle) => (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {bundle.name}
                </Typography>
                {actions(bundle)}
              </Box>
              {bundle.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {bundle.description}
                </Typography>
              )}
              <Box sx={{ mt: 1 }}>{itemSummary(bundle)}</Box>
            </Box>
          )}
        />
      </EntityListResults>

      <PartBundleFormDialog
        open={formState !== null}
        bundleId={formState?.bundleId ?? null}
        onClose={() => setFormState(null)}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title={t('bundles.deleteTitle')}
        message={t('bundles.deleteMessage', { name: toDelete?.name ?? '' })}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
        loading={deleteBundle.isPending}
      />
    </Box>
  );
}
